import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ScheduleModal from "../ScheduleModal/ScheduleModal";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import { pickTracksForClip, toUiTracks, parseTrackTitle } from "../../utils/musicMatch";
import "./ClipDetailPanel.css";

// Build a YouTube/source URL that jumps to the clip's start timecode.
// Mirrors the helper Coach Mode uses so behavior stays consistent.
const YT_HOSTS = ["youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be"];
function buildSourceLink(videoUrl, startSec) {
  if (!videoUrl) return null;
  const t = Math.floor(startSec || 0);
  try {
    const u = new URL(videoUrl);
    if (!YT_HOSTS.includes(u.hostname)) return videoUrl;
    if (u.hostname === "youtu.be") {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/watch?v=${id}&t=${t}s` : videoUrl;
    }
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube.com/watch?v=${v}&t=${t}s`;
    const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
    return m ? `https://www.youtube.com/watch?v=${m[1]}&t=${t}s` : videoUrl;
  } catch (_) {
    return videoUrl;
  }
}

const ClipDetailPanel = ({ clip, job, clipIndex, userPlan, catalog = [], onClose, onUpgrade, onDeleteClip, onPrev, onNext, hasPrev, hasNext }) => {
  const { t } = useTranslation(['clips', 'common']);
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const sidebarRef = useRef(null);
  const scrubberRef = useRef(null);
  const frameRef = useRef(null);
  // Direction of the last clip-swap ('up' = next/down-arrow, 'down' = prev),
  // used to slide the new clip in from the matching edge.
  const swipeDirRef = useRef("up");
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [currentVote, setCurrentVote] = useState(null);
  const [voting, setVoting] = useState(false);
  // Scrubber state — synced from the <video>'s timeupdate event.
  // `seeking` is true while the user is dragging the playhead so we
  // pause UI updates from timeupdate and let the drag set time directly.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const isFreeTrial = !userPlan || userPlan === "none";
  const isWidescreen = job.aspectRatio === "16:9";

  const hookText = clip.hook || "";
  const captionText = clip.caption || "";

  // Music recommendations — same strict audio-feature matcher Coach
  // Mode uses. Falls back to a clear empty-state message when either
  // side lacks data (no musicTarget on the clip, no audio features
  // in the catalog yet). See utils/musicMatch.js for the cascade.
  const matched = useMemo(
    () => pickTracksForClip(catalog, clip, 5),
    [catalog, clip]
  );
  const musicTracks = useMemo(() => toUiTracks(matched.tracks), [matched.tracks]);
  const musicStatusMsg = (() => {
    switch (matched.source) {
      case "no-spec": return "This clip is missing musicTarget — regenerate it to get vibe-matched music.";
      case "no-data": return "No catalog tracks have audio features yet — run the backfill script to enable matching.";
      case "no-pool": return "No catalog tracks tagged with this clip's inferred mood.";
      case "empty":   return "Catalog is loading…";
      default:        return "";
    }
  })();

  // Source link — jumps to the original video at the clip's start
  const sourceLink = buildSourceLink(job?.videoUrl, clip?.start_sec ?? clip?.start ?? 0);
  // Always show the pre-burned clip — never the clean version with overlay
  // Prefer the user's edited version when they've exported one
  // from /editor/ — it's the "latest" version of the clip.
  const videoSrc = clip.editedUrl || clip.url;
  // Poster = the clip's thumbnail (proxied) so swapping clips shows the frame
  // instead of a black flash while the new video decodes.
  const posterSrc = clip.thumbnailUrl
    ? `/api/clips/thumb?u=${encodeURIComponent(clip.thumbnailUrl)}`
    : undefined;

  // Smoothly slide the new clip in (from below on next, above on prev) whenever
  // the clip changes — no remount, so it restarts cleanly each swap.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof el.animate !== "function") return;
    const fromY = swipeDirRef.current === "up" ? "9%" : "-9%";
    el.animate(
      [
        { transform: `translateY(${fromY})`, opacity: 0.3 },
        { transform: "translateY(0)", opacity: 1 },
      ],
      { duration: 280, easing: "cubic-bezier(0.22, 0.61, 0.36, 1)" },
    );
  }, [videoSrc]);

  // Esc closes; ← / → / ↑ / ↓ navigate siblings if available.
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      else if ((e.key === "ArrowLeft" || e.key === "ArrowUp") && hasPrev)  { e.preventDefault(); swipeDirRef.current = "down"; onPrev?.(); }
      else if ((e.key === "ArrowRight" || e.key === "ArrowDown") && hasNext) { e.preventDefault(); swipeDirRef.current = "up"; onNext?.(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Auto-play whenever the clip changes — covers both the initial
  // panel open AND sibling navigation. Reset currentTime so the new
  // clip starts at the beginning, then wait a tick for the video
  // element to pick up the new src before calling .play().
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(0);
    // Some browsers reject .play() until they've buffered the new src;
    // a microtask gives them a chance to attach the source before we ask.
    Promise.resolve().then(() =>
      v.play().then(() => setIsPlaying(true)).catch(() => {
        // Unmuted autoplay was blocked (common on mobile) — fall back to muted
        // playback so the clip still plays; the volume/mute control unmutes it.
        v.muted = true;
        setIsMuted(true);
        v.play().then(() => setIsPlaying(true)).catch(() => {});
      }),
    );
  }, [clip?.url, clip?._id]);

  // Time tracking + duration. While the user is dragging the scrubber
  // we skip updates so the playhead doesn't fight the user's finger.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => { if (!seeking) setCurrentTime(v.currentTime); };
    const onMeta = () => setDuration(v.duration || 0);
    // Turning the volume up unmutes the clip; keep the button in sync with the
    // element's real state either way.
    const onVol = () => {
      if (v.volume > 0 && v.muted) v.muted = false;
      setIsMuted(v.muted || v.volume === 0);
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("durationchange", onMeta);
    v.addEventListener("volumechange", onVol);
    if (v.duration && !duration) setDuration(v.duration);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("durationchange", onMeta);
      v.removeEventListener("volumechange", onVol);
    };
  }, [seeking, duration]);

  // Track native fullscreen state so the button reflects what the
  // browser thinks (user can also press Esc to exit).
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    document.addEventListener("webkitfullscreenchange", onFs);
    return () => {
      document.removeEventListener("fullscreenchange", onFs);
      document.removeEventListener("webkitfullscreenchange", onFs);
    };
  }, []);

  // Map a clientX on the scrubber to a seek time
  const seekFromClientX = (clientX) => {
    const v = videoRef.current;
    const bar = scrubberRef.current;
    if (!v || !bar || !v.duration) return;
    const r = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    const t = pct * v.duration;
    v.currentTime = t;
    setCurrentTime(t);
  };

  const onScrubPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSeeking(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    seekFromClientX(e.clientX);
  };
  const onScrubPointerMove = (e) => {
    if (!seeking) return;
    e.preventDefault();
    seekFromClientX(e.clientX);
  };
  const onScrubPointerUp = (e) => {
    if (!seeking) return;
    setSeeking(false);
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) {}
  };

  // Native fullscreen toggle on the video frame. iOS Safari falls back
  // to webkitEnterFullscreen on the video element itself.
  const toggleFullscreen = (e) => {
    e?.stopPropagation?.();
    const frame = frameRef.current;
    const v = videoRef.current;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      return;
    }
    if (frame?.requestFullscreen) frame.requestFullscreen().catch(() => {});
    else if (frame?.webkitRequestFullscreen) frame.webkitRequestFullscreen();
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();   // iOS Safari
  };

  const fmtTime = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const total = Math.floor(t);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  };
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  // ── Mobile collapsing-hero scroll (iOS Music / Photos style) ────────────
  // As the user scrolls the sidebar up, the video area shrinks from its full
  // 50vh down to a compact 22vh. Scrolling back to the top expands it again.
  // The transform updates a CSS custom property so the CSS can interpolate
  // both height AND scale smoothly via transitions.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth > 700) return;        // mobile-only behavior
    const sb = sidebarRef.current;
    const ov = overlayRef.current;
    if (!sb || !ov) return;

    let ticking = false;
    const COLLAPSE_AT = 220;  // px of scroll until video is fully collapsed
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const t = Math.max(0, Math.min(1, sb.scrollTop / COLLAPSE_AT));
        ov.style.setProperty("--cdp-collapse", String(t));
        ticking = false;
      });
    };
    sb.addEventListener("scroll", onScroll, { passive: true });
    return () => sb.removeEventListener("scroll", onScroll);
  }, []);

  const formatDuration = (s) => {
    if (!s) return null;
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const formatTimecode = (s) => {
    if (s == null) return null;
    const total = Math.floor(s);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    return h > 0
      ? `${h}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`
      : `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const timecodeRange =
    clip.start_sec != null && clip.end_sec != null
      ? `${formatTimecode(clip.start_sec)} → ${formatTimecode(clip.end_sec)}`
      : null;

  const handleVideoClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setIsPlaying(true); }
    else { v.pause(); setIsPlaying(false); }
  };

  const toggleMute = (e) => {
    e?.stopPropagation?.();
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    if (!next) {
      if (v.volume === 0) v.volume = 1; // unmuting at zero volume = still silent
      if (v.paused) v.play().catch(() => {});
    }
    setIsMuted(next);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadProgress(0.2);
    try {
      const safeFilename = (hookText || `clip-${clipIndex + 1}`).replace(/[^a-zA-Z0-9-_ ]/g, "_") + ".mp4";
      // Prefer the editor's latest output (editedUrl) — fall back to
      // the original render if the user never edited. No client-side WebCodecs.
      const res = await axiosInstance.get("/clips/download-url", {
        params: { url: clip.editedUrl || clip.url, filename: hookText || `clip-${clipIndex + 1}` },
      });
      const a = document.createElement("a");
      a.href = res.data.url;
      a.download = safeFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setDownloadProgress(1);
    } catch (err) {
      toast.error(`Download failed: ${err.message || "Unknown error"}`);
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleVote = async (voteType) => {
    if (voting) return;
    setVoting(true);
    const newVote = currentVote === voteType ? null : voteType;
    const prev = currentVote;
    setCurrentVote(newVote);
    try {
      if (newVote) {
        await axiosInstance.post("/clips/feedback", { jobId: job._id, clipIndex, vote: newVote });
      } else {
        await axiosInstance.delete("/clips/feedback", { data: { jobId: job._id, clipIndex } });
      }
    } catch {
      setCurrentVote(prev);
    } finally {
      setVoting(false);
    }
  };

  return createPortal(
    <div className="cdp-overlay" ref={overlayRef}>
      {/* ── Floating close button — always visible ── */}
      <button className="cdp-float-close" onClick={onClose} aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* ── Sibling nav — TikTok-style up/down chevrons stacked on the right
             (↑ prev / ↓ next), plus ↑/↓ + ←/→ arrow keys. ── */}
      {(hasPrev || hasNext) && (
        <div className="cdp-nav-stack">
          <button
            type="button"
            className="cdp-nav cdp-nav--prev"
            onClick={(e) => { e.stopPropagation(); swipeDirRef.current = "down"; onPrev?.(); }}
            disabled={!hasPrev}
            aria-label="Previous clip"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 15 12 9 18 15" />
            </svg>
          </button>
          <button
            type="button"
            className="cdp-nav cdp-nav--next"
            onClick={(e) => { e.stopPropagation(); swipeDirRef.current = "up"; onNext?.(); }}
            disabled={!hasNext}
            aria-label="Next clip"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Left: video area ── */}
      <div
        className="cdp-video-area"
        onClick={onClose}
      >
        {/* Blurred video background instead of plain black */}
        <video
          className="cdp-video-bg"
          src={videoSrc}
          poster={posterSrc}
          muted
          autoPlay
          loop
          playsInline
          tabIndex={-1}
        />
        <div
          ref={frameRef}
          className={`cdp-video-frame${isWidescreen ? " cdp-video-frame--wide" : ""}${isFullscreen ? " cdp-video-frame--fs" : ""}`}
          onClick={(e) => { e.stopPropagation(); handleVideoClick(); }}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
        >
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            loop
            playsInline
            preload="auto"
            className="cdp-video"
          />

          {/* Play/pause hint */}
          <div className={`cdp-play-hint${showControls ? " visible" : ""}`}>
            {isPlaying ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            )}
          </div>

          {/* Mute / unmute — clips fall back to muted autoplay on mobile, so
              this is how you turn the sound on. */}
          <button
            className="cdp-mute-btn"
            onClick={toggleMute}
            title={isMuted ? "Unmute" : "Mute"}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 5 6 9H2v6h4l5 4V5z" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 5 6 9H2v6h4l5 4V5z" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              </svg>
            )}
          </button>

          {/* Fullscreen toggle — top-right of the video frame.
              Mobile: always visible. Desktop: only on hover. */}
          <button
            className="cdp-fs-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            aria-label="Toggle fullscreen"
          >
            {isFullscreen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
                <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
                <path d="M9 3H5a2 2 0 0 0-2 2v4" />
                <path d="M15 3h4a2 2 0 0 1 2 2v4" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9V5a2 2 0 0 1 2-2h4" />
                <path d="M21 9V5a2 2 0 0 0-2-2h-4" />
                <path d="M3 15v4a2 2 0 0 0 2 2h4" />
                <path d="M21 15v4a2 2 0 0 1-2 2h-4" />
              </svg>
            )}
          </button>

          {/* Scrubber — playhead + time. Drag-to-seek (touch + mouse).
              Click stops propagation so it doesn't pause the video. */}
          <div
            className="cdp-controls-bar"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="cdp-time">{fmtTime(currentTime)}</span>
            <div
              ref={scrubberRef}
              className="cdp-scrubber"
              onPointerDown={onScrubPointerDown}
              onPointerMove={onScrubPointerMove}
              onPointerUp={onScrubPointerUp}
              onPointerCancel={onScrubPointerUp}
            >
              <div className="cdp-scrubber-track">
                <div
                  className="cdp-scrubber-fill"
                  style={{ width: `${progressPct}%` }}
                />
                <div
                  className="cdp-scrubber-thumb"
                  style={{ left: `${progressPct}%` }}
                />
              </div>
            </div>
            <span className="cdp-time">{fmtTime(duration)}</span>
          </div>
        </div>
      </div>

      {/* ── Right: detail sidebar ── */}
      <div className="cdp-sidebar" ref={sidebarRef} onClick={(e) => e.stopPropagation()}>
        {/* Sticky drag-handle bar (visible on mobile only) — stays pinned
            during scroll so users always have a "this is a sheet" cue. */}
        <div className="cdp-sidebar-grip" aria-hidden="true">
          <span className="cdp-sidebar-grip-pill" />
        </div>
        {/* Header (close button moved to floating cdp-float-close above) */}
        <div className="cdp-sidebar-header">
          <span className="cdp-sidebar-eyebrow">{t('clips:clipDefaultTitle', { number: clipIndex + 1 })}</span>
        </div>

        {/* Caption — AI-generated, ready to paste when posting */}
        {captionText && (
          <div className="cdp-section cdp-section--caption">
            <div className="cdp-section-label">
              <span className="cdp-caption-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 12l2 2 4-4"/>
                  <circle cx="12" cy="12" r="10"/>
                </svg>
                AI caption
              </span>
              <button
                type="button"
                className="cdp-caption-copy-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(captionText);
                    toast.success("Caption copied!");
                  } catch {
                    toast.error("Couldn't copy — copy it manually");
                  }
                }}
                aria-label="Copy caption to clipboard"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy
              </button>
            </div>
            <p className="cdp-caption-text">{captionText}</p>
          </div>
        )}

        {/* Information */}
        <div className="cdp-section cdp-section--info">
          <div className="cdp-section-label">{t('clips:information')}</div>
          <div className="cdp-info-list">
            {timecodeRange && (
              <div className="cdp-info-row">
                <span className="cdp-info-key">Timecode</span>
                <span className="cdp-info-val">{timecodeRange}</span>
              </div>
            )}
            {clip.duration && (
              <div className="cdp-info-row">
                <span className="cdp-info-key">{t('clips:duration')}</span>
                <span className="cdp-info-val">{formatDuration(clip.duration)}</span>
              </div>
            )}
            {job.platform && (
              <div className="cdp-info-row">
                <span className="cdp-info-key">{t('clips:platform')}</span>
                <span className="cdp-info-val cdp-capitalize">{job.platform}</span>
              </div>
            )}
            {job.streamerName && (
              <div className="cdp-info-row">
                <span className="cdp-info-key">{t('clips:creator')}</span>
                <span className="cdp-info-val">{job.streamerName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Rate */}
        <div className="cdp-section cdp-section--rate">
          <div className="cdp-vote-row">
            <span className="cdp-section-label" style={{ margin: 0 }}>{t('clips:rateThisClip')}</span>
            <div className="cdp-vote-btns">
              <button
                className={`cdp-vote-btn${currentVote === "up" ? " up" : ""}`}
                onClick={() => handleVote("up")}
                disabled={voting}
              >
                <svg width="15" height="15" viewBox="0 0 24 24"
                  fill={currentVote === "up" ? "currentColor" : "none"}
                  stroke="currentColor" strokeWidth="2">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                </svg>
              </button>
              <button
                className={`cdp-vote-btn${currentVote === "down" ? " down" : ""}`}
                onClick={() => handleVote("down")}
                disabled={voting}
              >
                <svg width="15" height="15" viewBox="0 0 24 24"
                  fill={currentVote === "down" ? "currentColor" : "none"}
                  stroke="currentColor" strokeWidth="2">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Music recommendations — strict audio-feature matcher, same
            engine Coach Mode uses. When the clip lacks musicTarget or
            the catalog lacks audio features, we render an inline
            status message instead of falling back to off-vibe tracks. */}
        {(musicTracks.length > 0 || musicStatusMsg) && (
          <div className="cdp-section cdp-section--music">
            <div className="cdp-section-label cdp-section-label--music">
              <span className="cdp-music-badge">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                Music to pair{musicTracks.length > 0 && matched.mood ? ` · ${matched.mood}` : ""}
              </span>
            </div>
            {musicTracks.length === 0 ? (
              <div className="cdp-music-empty">{musicStatusMsg}</div>
            ) : (
              <div className="cdp-music-row">
                {musicTracks.map((t, i) => {
                  const { displayName, modifiers } = parseTrackTitle(t.trackName);
                  return (
                    <div key={t.trackId || i} className="cdp-music-card">
                      <div className="cdp-music-meta">
                        <div className="cdp-music-name">{displayName}</div>
                        {t.artist && <div className="cdp-music-artist">{t.artist}</div>}
                        {modifiers.length > 0 && (
                          <div className="cdp-music-mods">
                            {modifiers.map((m) => (
                              <span key={m} className="cdp-music-mod">{m}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="cdp-music-links">
                        {t.tiktokSoundUrl && (
                          <a href={t.tiktokSoundUrl} target="_blank" rel="noopener noreferrer" className="cdp-music-link">TikTok</a>
                        )}
                        {t.spotifyUrl && (
                          <a href={t.spotifyUrl} target="_blank" rel="noopener noreferrer" className="cdp-music-link">Spotify</a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Spacer pushes actions to bottom */}
        <div className="cdp-spacer" />

        {/* Actions */}
        <div className="cdp-actions">
          {/* Primary row: Publish Now + Download */}
          <div className="cdp-btn-row-primary">
            <button
              className="cdp-btn-orange"
              onClick={() => {
                if (isFreeTrial) { onUpgrade?.(); return; }
                setShowScheduleModal(true);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Publish Now
            </button>
            <button
              className="cdp-btn-orange"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? (
                <>
                  <span className="cdp-spinner" />
                  {Math.round(downloadProgress * 100)}%
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t('common:download')}
                </>
              )}
            </button>
          </div>

          {/* Edit clip — opens the OpenReel-based editor at /editor/.
              Hover triggers a low-priority prefetch of the video so the
              browser cache is warm by the time the user clicks; the
              editor's own fetch then comes from cache (sub-50ms) and
              feels instant instead of the ~1-3s download blocking. */}
          {/* Editable clips: overlays aren't loaded in the list query anymore
              (payload trim) — the editor fetches them fresh. Show Edit for any
              clip with a URL; overlays present (single-clip fetches) still count. */}
          {clip?.url && (clip.overlays == null || clip.overlays.length > 0) && (
            <a
              className="cdp-btn-edit"
              href={`/edit/${job?._id}/${clipIndex}`}
              onMouseEnter={() => {
                const videoUrl = clip.cleanUrl || clip.url;
                if (!videoUrl) return;
                // Low-priority fetch warms the HTTP cache without
                // blocking anything else on the page. Browser dedupes
                // if the editor later fetches the same URL.
                try {
                  fetch(videoUrl, { mode: "cors", credentials: "omit", priority: "low" }).catch(() => {});
                } catch { /* no-op */ }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Edit clip
            </a>
          )}

          {/* Secondary row — Content link (jumps to source video at the
              clip's start timecode) + Delete sharing the same line so
              they read as a single "side actions" cluster. */}
          <div className="cdp-btn-row-secondary">
            {sourceLink && (
              <a
                href={sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                className="cdp-btn-link-small"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Content link
              </a>
            )}
            <button className="cdp-btn-delete-small" onClick={() => onDeleteClip?.()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              {t('common:delete')}
            </button>
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleModal
          clip={clip}
          clipJobId={job._id}
          clipIndex={clipIndex}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => setShowScheduleModal(false)}
          initialAction="schedule"
        />
      )}
    </div>,
    document.body
  );
};

export default ClipDetailPanel;
